/**
 * Owner: Masa Bayu
 * File: feature-flags.ts
 * Date:Time: 2026-10-15 10:23
 * Description: UE flags · FaithfulSeg/P5 default ON (Owner Open) · opt-out=0.
 */

/**
 * Face + Lexical ladder wired · package remains PoC under Architecture Reset 01.
 * Not a Production Ready certificate.
 */
export const USE_UNIVERSAL_ENGINE = true;

/**
 * Rendering Reset cutover · Face + Edit share this memo (G15).
 * R11.7 · default `v2` · rollback `v1`/`legacy` via env (G12).
 * Universal claim = Latin only (G28/G52) · non-Latin runtime → v1 (G51).
 */
export type RenderPipe = 'legacy' | 'v1' | 'v2';

/** Minimal tip linePaint shape for G51 + R11-LINE-02 gate. */
export type LinePaintScriptGate = {
  readonly script?: string;
  readonly direction?: string;
  readonly extension?: string;
  readonly segments?: readonly unknown[];
  readonly wordStarts?: readonly number[];
};

let cachedRenderPipe: RenderPipe | undefined;
/** FaithfulSeg/P5 · browser default ON · opt-out `?qxFaithfulSeg=0`. */
let faithfulSegOptIn: boolean | undefined;

/** Latin allow-list only (no extension check) · R11.5 LINE-01 edit measure. */
export function isLatinLinePaintAllow(
  linePaint: LinePaintScriptGate | null | undefined,
): boolean {
  if (!linePaint) return false;
  const script = linePaint.script;
  if (script === 'latin') return true;
  if (script === 'arabic' || script === 'cjk' || script === 'mixed') {
    return false;
  }
  if (linePaint.direction === 'rtl' || linePaint.direction === 'ttb') {
    return false;
  }
  return true;
}

function readRenderPipeEnv(): string | undefined {
  /*
   * Vite only statically replaces direct `import.meta.env.VITE_*` access.
   * Optional chaining / dynamic keys stay undefined at runtime.
   */
  try {
    const vitePipe = import.meta.env.VITE_QX_RENDER_RESET_PIPE;
    if (typeof vitePipe === 'string' && vitePipe.length > 0) return vitePipe;
  } catch {
    /* import.meta.env unavailable */
  }
  if (
    typeof process !== 'undefined' &&
    process.env &&
    typeof process.env.QX_RENDER_RESET_PIPE === 'string' &&
    process.env.QX_RENDER_RESET_PIPE.length > 0
  ) {
    return process.env.QX_RENDER_RESET_PIPE;
  }
  try {
    const meta = import.meta as ImportMeta & {
      readonly env?: Record<string, string | undefined>;
    };
    const fromMeta = meta.env?.QX_RENDER_RESET_PIPE;
    if (typeof fromMeta === 'string' && fromMeta.length > 0) return fromMeta;
  } catch {
    /* import.meta.env unavailable */
  }
  return undefined;
}

function resolveRenderPipe(raw: string | undefined): RenderPipe {
  // Explicit rollback only · everything else (incl. unset) → v2 (R11.7).
  if (raw === 'legacy' || raw === 'v1') return raw;
  return 'v2';
}

/**
 * R11.7e · v2 L2 segment paint gate (fail-closed).
 * - extension R11-LINE-02 + non-empty segments required
 * - R11-LINE-01 / wordStarts-only → false (falls to v1 under v2 pipe)
 * - Latin allow-list (G51) still required
 */
export function shouldUseLinePaintV2(
  linePaint: LinePaintScriptGate | null | undefined,
): boolean {
  if (!linePaint) return false;
  if (linePaint.extension !== 'R11-LINE-02') return false;
  if (!Array.isArray(linePaint.segments) || linePaint.segments.length === 0) {
    return false;
  }
  return isLatinLinePaintAllow(linePaint);
}

/**
 * FaithfulSeg / P5 hybrid (Owner Open 2026-10-15 10:23).
 * Browser default ON · LINE-02 tip + P5 pdf.js underlay.
 * Opt-out: `?qxFaithfulSeg=0` or `window.__QX_FAITHFUL_SEG__ === false`.
 * Non-DOM (Node/tests without window): OFF (fail-closed for measures).
 */
export function isFaithfulSegOptIn(): boolean {
  if (faithfulSegOptIn !== undefined) return faithfulSegOptIn;
  try {
    if (typeof window !== 'undefined') {
      const w = window as Window & { __QX_FAITHFUL_SEG__?: boolean };
      if (w.__QX_FAITHFUL_SEG__ === false) {
        faithfulSegOptIn = false;
        return false;
      }
      if (w.__QX_FAITHFUL_SEG__ === true) {
        faithfulSegOptIn = true;
        return true;
      }
      if (typeof w.location?.search === 'string') {
        const q = new URLSearchParams(w.location.search);
        if (q.get('qxFaithfulSeg') === '0') {
          faithfulSegOptIn = false;
          return false;
        }
        if (q.get('qxFaithfulSeg') === '1') {
          faithfulSegOptIn = true;
          return true;
        }
      }
      // Owner Open · flip default FaithfulSeg/P5
      faithfulSegOptIn = true;
      return true;
    }
  } catch {
    /* non-DOM */
  }
  faithfulSegOptIn = false;
  return false;
}

/**
 * Rendering Reset cutover.
 * Default `v2` (R11.7) · `v1`/`legacy` env rollback · G15 memoized.
 * FaithfulSeg ON forces v2 so LINE-02 segments can paint when present.
 * Explicit rollback is read first · FaithfulSeg defaults ON in any DOM, so
 * checking it first would make the G12 rollback lever unreachable.
 */
export function getRenderPipe(): RenderPipe {
  if (cachedRenderPipe !== undefined) return cachedRenderPipe;
  const raw = readRenderPipeEnv();
  if (raw === 'legacy' || raw === 'v1') {
    cachedRenderPipe = raw;
    return cachedRenderPipe;
  }
  cachedRenderPipe = isFaithfulSegOptIn() ? 'v2' : resolveRenderPipe(raw);
  return cachedRenderPipe;
}

/** Test-only · clear memo so env changes are visible. */
export function __resetRenderPipeForTests(): void {
  cachedRenderPipe = undefined;
  faithfulSegOptIn = undefined;
}

/**
 * WS-9 · Accept Law extract alignment (visualLayer + dual-paint guard).
 * Set false for emergency rollback to pre-align Eyes→SPSU path.
 */
export const USE_UNIVERSAL_ALIGN = true;

/**
 * Pre-Universal `@qx/qx-editor` runQXEditor entry.
 * false = callers use runQXEditorGated · retain runQXEditor until Owner decommission.
 */
export const USE_LEGACY_QXEDITOR = false;

/**
 * Face canvas via Universal renderers (paged read / Lexical edit).
 * false = legacy absolute SPSU Face layers (rollback).
 */
export const USE_UNIVERSAL_FACE = true;

/**
 * WS-14.4 word-split / FrequencyWords repair.
 * 🔐 ARCHITECTURE RESET 01 · LOCKED OFF
 * Do not set true until Canonical Document Model + contracts + corpus gates
 * are Owner-Accepted. See QX-UNIVERSAL-ENGINE-ARCHITECTURE-RESET-01.md.
 */
export const USE_WORD_SPLIT_REPAIR = false;

/**
 * GATE-USGM-FAIL-CLOSED-SLICE · Mode B LIVE EXECUTE (Owner 2026-10-08 10:09).
 * Narrow hold only: Align crumb silent-drop + OCR word-split repair EXECUTE.
 * ≠ CRUMB-CUT · ≠ Phase 5 · ≠ RESET-01 CLOSE · ≠ UE-09/UE-12 · ≠ Global SoT.
 * false = emergency rollback to pre-Mode-B illicit EXECUTE (Owner only).
 */
export const USE_FAIL_CLOSED_SLICE_ALPHA = true;
